/**
 * Merlin Extensions - Theme Manager
 * 
 * Handles theme switching (dark/light) with persistence.
 * Syncs theme across Dash Mantine and extension components.
 */

import { storage } from './browser-api.js';

const THEME_KEY = 'merlin_theme';
const THEME_ATTRIBUTE = 'data-theme';

let currentTheme = 'dark';
let listeners = [];

/**
 * Initialize theme from storage
 */
async function initTheme() {
    try {
        const result = await storage.get([THEME_KEY]);
        currentTheme = result[THEME_KEY] || 'dark';
        applyTheme(currentTheme);
    } catch (error) {
        console.error('Error loading theme:', error);
        applyTheme('dark');
    }
}

/**
 * Apply theme to document
 * @param {string} theme - 'dark' or 'light'
 */
function applyTheme(theme) {
    currentTheme = theme;
    
    // Set on document root for CSS variable switching
    document.documentElement.setAttribute(THEME_ATTRIBUTE, theme);
    
    // Add/remove class for legacy CSS selectors
    document.documentElement.classList.remove('light-theme', 'dark-theme');
    document.documentElement.classList.add(`${theme}-theme`);
    
    // Sync with Mantine if available
    if (window.dash_mantine_components) {
        // Mantine v7 uses colorScheme
        const mantineRoot = document.querySelector('[data-mantine-color-scheme]');
        if (mantineRoot) {
            mantineRoot.setAttribute('data-mantine-color-scheme', theme);
        }
    }
    
    // Notify listeners
    listeners.forEach(callback => callback(theme));
}

/**
 * Get current theme
 * @returns {string} Current theme ('dark' or 'light')
 */
function getTheme() {
    return currentTheme;
}

/**
 * Set theme with persistence
 * @param {string} theme - 'dark' or 'light'
 */
async function setTheme(theme) {
    applyTheme(theme);
    try {
        await storage.set({ [THEME_KEY]: theme });
    } catch (error) {
        console.error('Error saving theme:', error);
    }
}

/**
 * Toggle between dark and light themes
 */
async function toggleTheme() {
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    await setTheme(newTheme);
    return newTheme;
}

/**
 * Subscribe to theme changes
 * @param {function} callback - Called with new theme when it changes
 * @returns {function} Unsubscribe function
 */
function onThemeChange(callback) {
    listeners.push(callback);
    return () => {
        listeners = listeners.filter(cb => cb !== callback);
    };
}

/**
 * Get CSS variable value for current theme
 * @param {string} varName - CSS variable name (without --)
 * @returns {string} Computed value
 */
function getCSSVar(varName) {
    return getComputedStyle(document.documentElement)
        .getPropertyValue(`--${varName}`)
        .trim();
}

// Auto-initialize on DOM ready
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initTheme);
    } else {
        initTheme();
    }
}

// Export for both ES modules and browser globals
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { initTheme, applyTheme, getTheme, setTheme, toggleTheme, onThemeChange, getCSSVar };
}

if (typeof window !== 'undefined') {
    window.MerlinTheme = { initTheme, applyTheme, getTheme, setTheme, toggleTheme, onThemeChange, getCSSVar };
}

export { initTheme, applyTheme, getTheme, setTheme, toggleTheme, onThemeChange, getCSSVar };
